import { Button, Search, Switch } from '@ibsys/kit';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useHasPermission } from '@/entities/profile';
import { SmartSearchInput, useSmartSearchInfinite } from '@/features/smart-search';
import type { KnowledgeSetListItem } from '@/entities/knowledge-set';
import { useAppNavigate } from '@/shared/lib';
import { useDebouncedValue } from '@/shared/hooks';
import { KnowledgeSetList } from './KnowledgeSetList';
import { KnowledgeSetCard } from './KnowledgeSetCard';
import { MasterDetailLayout } from '@/shared/ui';

export const KnowledgeSetsPage: FC = () => {
  const { t } = useTranslation(['common', 'knowledgeSets']);

  const { id: idOrCreating } = useParams<{ id?: string }>();
  const isCreating = idOrCreating === 'create';
  const id = idOrCreating && !isCreating ? +idOrCreating : undefined;

  const navigate = useAppNavigate();
  const canEdit = useHasPermission('ADMIN_KNOWLEDGE');

  const [searchParams, setSearchParams] = useSearchParams();
  const showDeleted = searchParams.get('deleted') === 'true';

  // Поиск по смыслу — реактивное поле; умный поиск (предикат) коммитится отдельно по submit
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);

  const { searchState, items, isLoading, fetchNextPage, hasNextPage, predicateInfo, handleSearch, handleClear } =
    useSmartSearchInfinite<'knowledge_set', KnowledgeSetListItem>({
      entityType: 'knowledge_set',
      baseFilters: { isDeleted: showDeleted ? undefined : false },
      search: debouncedSearchQuery,
      pageSize: 50,
    });

  const handleClickAdd = () => {
    navigate('/knowledge-sets/create');
  };

  const handleBack = () => {
    navigate('/knowledge-sets', undefined, { saveParams: true });
  };

  const handleShowDeletedToggle = useCallback(
    (state: boolean) => {
      setSearchParams((prev) => {
        if (state) prev.set('deleted', 'true');
        else prev.delete('deleted');
        return prev;
      });
    },
    [setSearchParams],
  );

  return (
    <MasterDetailLayout
      isDetailOpen={Boolean(idOrCreating)}
      onBack={handleBack}
      toolbar={
        <>
          <div className="flex items-center gap-4">
            <Search
              className="flex-1"
              placeholder={t('knowledgeSets:searchByMeaningPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {canEdit && (
              <Button onClick={handleClickAdd} className="ml-auto">
                {t('knowledgeSets:addKnowledgeSet')}
              </Button>
            )}
          </div>

          <SmartSearchInput
            value={searchState.value}
            onChange={searchState.onChange}
            mode={searchState.searchMode}
            onModeChange={searchState.setSearchMode}
            onSearch={handleSearch}
            onClear={handleClear}
            predicateInfo={predicateInfo}
            isLoading={isLoading}
            placeholder={t('knowledgeSets:smartSearchPlaceholder')}
          />

          <Switch value={showDeleted} onChange={handleShowDeletedToggle} label={t('common:showDeleted')} />
        </>
      }
      list={
        <KnowledgeSetList
          items={items}
          isLoading={isLoading}
          hasNextPage={hasNextPage ?? false}
          onLoadMore={fetchNextPage}
        />
      }
      detail={<KnowledgeSetCard key={id} knowledgeSetId={id} isCreating={isCreating} />}
      empty={
        <div className="flex items-center justify-center h-full text-muted-foreground">
          {t('knowledgeSets:selectKnowledgeSet')}
        </div>
      }
    />
  );
};
